import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

import Input from "../../../components/form-item/Input";
import Toggle from "../../../components/form-item/Toggle";
import Stepper from "../../../components/stepper/Stepper";
import UserItem from "../../../components/user-item/UserItem";
import Modal, { type ModalAction } from "../../../components/modal/Modal";
import ApprovalControl from "../../../components/approval-control/ApprovalControl";
// @ts-expect-error - JSファイルの型定義なし
import PersonalInformation from "../../my-page/components/PersonalInformation";
// @ts-expect-error - JSファイルの型定義なし
import BankAccount from "../../my-page/components/BankAccount";
import {
  CARD_WIDTH_PX,
  CARD_HEIGHT_PX,
} from "../../../components/offer-wizard/OfferUserCard";
import OfferSummaryCard, {
  type OfferSummaryCardData,
} from "../../../components/offer-wizard/OfferSummaryCard";
import SubcontractContractPdfPreview, {
  type SubcontractPdfPreviewState,
} from "../../../components/offer-wizard/SubcontractContractPdfPreview";

import { useRegisterFromInvitationMutation } from "../../../app/services/invitations";
import {
  useGetDmThreadDetailQuery,
  useUpdateDmThreadMutation,
} from "../../../app/services/dmThreads";
import {
  requestOfferSubcontractPdf,
  formatPdfExportError,
} from "../../../app/services/pdfExports";
// @ts-expect-error - JS ファイルの型定義なし
import { userApi, useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import {
  setAuthTokenToCookies,
  removeAuthTokenFromCookies,
} from "../../../common/utils";
import { extractErrorMessage } from "../inviteUtils";

// ===== Types =====

type OnboardingStep =
  | "terms"
  | "password"
  | "account"
  | "payment"
  | "contract";

/**
 * 書類確認ステップで契約書 PDF の生成に失敗したときのフォールバック表示
 * （OfferSummaryCard）に使うオファー条件の控え。主たる値は DM Detail API から
 * 取得し、ここでは verify 由来の最小限（ロール / 報酬 / 納期）だけを保持する。
 */
export interface OfferOnboardingSummary {
  /** 「ロール（スコープ）」サマリ。 */
  role?: string;
  /** 報酬（税込合計）。 */
  reward?: number | null;
  /** 納期（ISO 8601）。 */
  deadline?: string;
}

interface OfferOnboardingWizardProps {
  token: string;
  email: string;
  projectId: string;
  offerId: string;
  summary: OfferOnboardingSummary;
  /** 完了（承諾済み）後にプロジェクト / オファースレッドへ遷移する。 */
  onComplete: (projectId: string, offerId?: string) => void;
}

// ===== Styled =====

const SectionTitle = styled.h2.attrs({ className: "heading-spacing-1824" })`
  margin-bottom: 8px;
  color: #000;
`;

const Description = styled.p.attrs({ className: "bodytext u-line-height-150" })`
  color: #000;
  white-space: pre-wrap;
`;

const TermsContent = styled.div`
  margin: 24px 0;
  padding: 24px;
  background-color: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
`;

// アカウントステップのユーザーカード（UserItem variant="card"）を、オファー作成時と
// 同じ寸法（白銀比）で中央に置くためのスロット。
const AccountCardSlot = styled.div`
  margin: 24px auto 0;
  width: ${CARD_WIDTH_PX}px;
  height: ${CARD_HEIGHT_PX}px;
  max-width: 100%;
`;

const ErrorBox = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 16px;
`;

// 書類確認ステップの長押し承認（ApprovalControl）を中央に置く行。
const ApprovalRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

// ===== ステップ定義 =====

const STEP_DEFINITIONS: Array<{ id: OnboardingStep; label: string }> = [
  { id: "terms", label: "利用許諾" },
  { id: "password", label: "パスワード" },
  { id: "account", label: "アカウント" },
  { id: "payment", label: "振込先" },
  { id: "contract", label: "書類確認" },
];
const STEPS: OnboardingStep[] = STEP_DEFINITIONS.map((s) => s.id);
const STEP_LABELS: string[] = STEP_DEFINITIONS.map((s) => s.label);

// ===== Component =====

const fade = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 },
  transition: { duration: 0.3 },
};

const OfferOnboardingWizard: React.FC<OfferOnboardingWizardProps> = ({
  token,
  email,
  projectId,
  offerId: initialOfferId,
  summary,
  onComplete,
}) => {
  const dispatch = useDispatch();
  const [registerFromInvitation, { isLoading: isRegistering }] =
    useRegisterFromInvitationMutation();
  // 書類確認ステップの引き受け（承諾）は DM の受領モーダルと同じ updateDmThread を使う。
  const [updateDmThread, { isLoading: isAccepting }] =
    useUpdateDmThreadMutation();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>("terms");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [registeredUserId, setRegisteredUserId] = useState<number | null>(null);
  const [offerId, setOfferId] = useState<string>(initialOfferId);
  const [isRegistered, setIsRegistered] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  // 書類確認ステップの長押し承認（ApprovalControl）が完了したか。
  const [isContractConfirmed, setIsContractConfirmed] = useState(false);

  const [pdfPreview, setPdfPreview] = useState<SubcontractPdfPreviewState | null>(
    null,
  );

  const currentStepIndex = STEPS.indexOf(currentStep);

  const goNext = useCallback(() => {
    const idx = STEPS.indexOf(currentStep);
    if (idx < STEPS.length - 1) setCurrentStep(STEPS[idx + 1]);
  }, [currentStep]);

  const goBack = useCallback(() => {
    const idx = STEPS.indexOf(currentStep);
    // 登録（アカウント作成）後は password / terms へ戻れない（再登録で詰まるため）。
    // 登録後の最小ステップは「アカウント」(account) とする。
    const minIdx = isRegistered ? STEPS.indexOf("account") : 0;
    if (idx > minIdx) setCurrentStep(STEPS[idx - 1]);
  }, [currentStep, isRegistered]);

  // /api/me（登録後にのみ取得）。プロフィールプレビューの描画と、契約・支払い
  // ステップの「本名」入力チェックに使う。フォーム自体は my-page の
  // PersonalInformation / BankAccount が直接 /api/me を読み書きする。
  const { data: currentUserData } = useGetCurrentUserQuery(undefined, {
    skip: !isRegistered,
  });

  // 契約確認ステップに到達したら、入力済みの契約者情報で契約ドラフト PDF を生成する。
  const { data: threadDetail } = useGetDmThreadDetailQuery(
    { project_id: projectId, dm_id: offerId },
    { skip: currentStep !== "contract" || !offerId },
  );

  const generateContractPdf = useCallback(async () => {
    if (!offerId) return;
    setPdfPreview({ status: "generating" });
    try {
      const { blob, filename } = await requestOfferSubcontractPdf({
        dmId: offerId,
      });
      setPdfPreview({ status: "ready", blob, filename });
    } catch (e) {
      setPdfPreview({
        status: "error",
        errorMessage: formatPdfExportError(e, {
          forbiddenMessage:
            "契約書を表示する権限がありません。お手数ですが時間をおいて再度お試しください。",
        }),
      });
    }
  }, [offerId]);

  useEffect(() => {
    if (currentStep === "contract" && offerId && pdfPreview === null) {
      void generateContractPdf();
    }
  }, [currentStep, offerId, pdfPreview, generateContractPdf]);

  const isPasswordValid =
    password.length >= 8 && password === passwordConfirm;

  // パスワードステップ: 招待経由の新規登録 API を叩き、新規ユーザーのトークンで
  // 認証状態を差し替えてからアカウントステップへ進む。
  const handleRegister = useCallback(async () => {
    // すでに登録済み（戻って再表示された等）なら再登録せず次へ進む（409 回避）。
    if (isRegistered) {
      goNext();
      return;
    }
    setSubmitError("");
    try {
      removeAuthTokenFromCookies();
      const defaultDisplayName = email.split("@")[0] || "User";

      const formData = new FormData();
      formData.append("token", token);
      formData.append("password", password);
      formData.append("terms_accepted", "true");
      formData.append("display_name", defaultDisplayName);

      const result = await registerFromInvitation(formData).unwrap();
      if (!result.token) {
        throw new Error(
          "登録は完了しましたが、認証情報の取得に失敗しました。ページを再読み込みしてサインインしてください。",
        );
      }
      setAuthTokenToCookies(result.token);
      dispatch(userApi.util.resetApiState());
      setRegisteredUserId(result.user_id);
      if (result.offer_id) setOfferId(result.offer_id);
      setIsRegistered(true);
      goNext();
    } catch (err) {
      setSubmitError(
        extractErrorMessage(err, "登録に失敗しました。もう一度お試しください。"),
      );
    }
  }, [isRegistered, token, password, email, registerFromInvitation, dispatch, goNext]);

  // 契約・支払いステップから次へ進む。フォーム（PersonalInformation / BankAccount）が
  // 各フィールドを /api/me に自動保存しているため、ここでは契約確認ステップ用に
  // PDF を再生成させてから遷移するだけでよい。
  const handlePaymentNext = useCallback(() => {
    setSubmitError("");
    setPdfPreview(null);
    // 情報を修正して書類確認へ入り直した場合は、長押し承認をやり直してもらう。
    setIsContractConfirmed(false);
    goNext();
  }, [goNext]);

  // 書類確認ステップの「引き受ける」: DM の受領モーダル（OfferReceiveModal /
  // DM.handleAcceptOffer）と全く同じ承諾処理を行う。長押し承認のうえで実行し、
  // 取引成立（status 遷移）に成功してから DM（トークルーム）へ遷移する。
  const handleAccept = useCallback(async () => {
    if (!offerId) return;
    setSubmitError("");
    const requiresDeliverable =
      threadDetail?.data?.thread?.requires_deliverable;
    try {
      await updateDmThread({
        project_id: projectId,
        dm_id: offerId,
        // 成果物不要のオファーは受諾と同時に検収待ちへ直行（DM 側と同一の分岐）。
        status:
          requiresDeliverable === false ? "inspection_pending" : "in_progress",
      }).unwrap();
      onComplete(projectId, offerId);
    } catch (err) {
      setSubmitError(
        extractErrorMessage(
          err,
          "オファーの引受に失敗しました。時間をおいて再度お試しください。",
        ),
      );
    }
  }, [offerId, projectId, threadDetail, updateDmThread, onComplete]);

  // 書類確認ステップのサマリ（PDF 生成に失敗したときのフォールバック表示）。
  const contractSummary: OfferSummaryCardData = useMemo(() => {
    const t = threadDetail?.data?.thread;
    return {
      assignedRoles: t?.assigned_roles ?? summary.role ?? null,
      scope: t?.scope ?? null,
      deadline: t?.deadline ?? summary.deadline ?? null,
      contractAmount: t?.contract_amount ?? summary.reward ?? null,
      acceptPosition: t?.accept_position ?? null,
      requiresDeliverable: t?.requires_deliverable ?? null,
      allowSubcontracting: t?.allow_subcontracting ?? null,
      initialMessage: t?.initial_message ?? null,
    };
  }, [threadDetail, summary]);

  // ===== ステップ描画 =====

  const renderStepContent = () => {
    switch (currentStep) {
      case "terms":
        return (
          <motion.div key="terms" {...fade}>
            <SectionTitle>利用規約を確認する</SectionTitle>
            <Description>
              SOREMO OSでオファーを確認・承諾するには、利用規約とプライバシーポリシーへの同意が必要です。
            </Description>
            <TermsContent>
              <div className="bodytext u-line-height-150">
                <p style={{ marginBottom: 16 }}>
                  <strong>SOREMO サービス利用規約</strong>
                </p>
                <p style={{ marginBottom: 12 }}>
                  本規約は、株式会社ソレモ（以下「当社」）が提供するサービス「SOREMO」（以下「本サービス」）の利用条件を定めるものです。
                </p>
                <p style={{ marginBottom: 12 }}>
                  第1条（適用）
                  <br />
                  本規約は、本サービスの利用に関する当社とユーザーとの間の権利義務関係を定めることを目的とし、ユーザーと当社との間の本サービスの利用に関わる一切の関係に適用されます。
                </p>
                <p style={{ marginBottom: 12 }}>
                  第2条（利用登録）
                  <br />
                  登録希望者が当社の定める方法によって利用登録を申請し、当社がこれを承認することによって、利用登録が完了するものとします。
                </p>
                <p>
                  第3条（個人情報の取り扱い）
                  <br />
                  当社は、ユーザーの個人情報を当社のプライバシーポリシーに従い適切に取り扱います。
                </p>
              </div>
            </TermsContent>
            <Toggle
              value={termsAccepted}
              onClick={() => setTermsAccepted(!termsAccepted)}
            >
              <span className="bodytext">
                <a
                  href="/privacypolicy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  style={{
                    color: "var(--soremo-blue)",
                    textDecoration: "none",
                    fontSize: "inherit",
                  }}
                >
                  利用規約・プライバシーポリシー
                </a>
                に同意します
              </span>
            </Toggle>
          </motion.div>
        );

      case "password":
        return (
          <motion.div key="password" {...fade}>
            <SectionTitle>パスワードを設定する</SectionTitle>
            <Description>サインインに使用するパスワードを設定してください。</Description>
            <div
              className="bodytext-13 u-text-light-gray"
              style={{ marginTop: 8, marginBottom: 32 }}
            >
              メールアドレス: {email}
            </div>
            <div className="u-w100 u-col u-gap16">
              <Input
                label="パスワード"
                option="required"
                type="password"
                autocomplete="new-password"
                placeholder="8文字以上のパスワード"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                error={
                  password && password.length < 8
                    ? "パスワードは8文字以上で設定してください"
                    : ""
                }
              />
              <Input
                label="パスワード確認"
                option="required"
                type="password"
                autocomplete="new-password"
                placeholder="もう一度入力してください"
                value={passwordConfirm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPasswordConfirm(e.target.value)
                }
                error={
                  passwordConfirm && password !== passwordConfirm
                    ? "パスワードが一致しません"
                    : ""
                }
              />
            </div>
            {submitError && <ErrorBox>{submitError}</ErrorBox>}
          </motion.div>
        );

      case "account":
        return (
          <motion.div key="account" {...fade}>
            <SectionTitle>アカウントを設定する</SectionTitle>
            <Description>
              SOREMO OS上に表示されるアカウントです。オファー相手やプロジェクトメンバーに表示されます。
              {"\n"}カードのアバターまたは名前・タイトルをタップすると編集できます。英語表記やアバターは、あとからマイページでも設定できます。
            </Description>
            <AccountCardSlot>
              {/* オファー作成時と同じユーザーカードのプレビュー。アバター / テキストを
                  タップすると編集モーダルが開く（UserItem variant="card"）。 */}
              <UserItem
                size={96}
                editable={true}
                hideBadges={true}
                variant="card"
                expectedUserId={registeredUserId ?? undefined}
                expectedEmail={email}
              />
            </AccountCardSlot>
          </motion.div>
        );

      case "payment":
        return (
          <motion.div key="payment" {...fade}>
            <SectionTitle>振込先・契約者情報を入力する</SectionTitle>
            <Description>
              契約書と支払いに必要な情報を入力してください。この情報は、アカウントには公開されません。
            </Description>
            {/* マイページと同一のフォーム部品を再利用（各項目を /api/me に自動保存）。 */}
            <div style={{ marginTop: 24 }}>
              <PersonalInformation />
            </div>
            <div style={{ marginTop: 48 }}>
              <BankAccount />
            </div>
            {submitError && <ErrorBox>{submitError}</ErrorBox>}
          </motion.div>
        );

      case "contract":
        return (
          <motion.div key="contract" {...fade}>
            <SectionTitle>書類を確認する</SectionTitle>
            <Description>
              入力いただいた契約・支払い情報をもとに、契約書を作成しました。内容を確認して承認すると、オファーを引き受けます。
            </Description>
            <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
              {pdfPreview && (
                <SubcontractContractPdfPreview
                  preview={pdfPreview}
                  onRetry={() => {
                    setPdfPreview(null);
                    void generateContractPdf();
                  }}
                />
              )}
              {(!pdfPreview || pdfPreview.status === "error") && (
                <OfferSummaryCard data={contractSummary} showRecipient={false} />
              )}
              {submitError && <ErrorBox>{submitError}</ErrorBox>}
              {/* DM の受領モーダルと同じ長押し承認。承認後に「引き受ける」が押せる。 */}
              <ApprovalRow>
                <ApprovalControl
                  onConfirm={() => setIsContractConfirmed(true)}
                  onConfirmedChange={setIsContractConfirmed}
                  disabled={isAccepting}
                />
              </ApprovalRow>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  // ===== フッターアクション =====

  const getPrimaryAction = (): ModalAction => {
    switch (currentStep) {
      case "terms":
        return {
          label: "同意して進む",
          onClick: goNext,
          disabled: !termsAccepted,
          rightIcon: "chevron_right",
        };
      case "password":
        return {
          label: "登録して進む",
          onClick: handleRegister,
          disabled: !isPasswordValid,
          loading: isRegistering,
          rightIcon: isRegistering ? undefined : "chevron_right",
        };
      case "account":
        return {
          label: "次へ",
          onClick: goNext,
          rightIcon: "chevron_right",
        };
      case "payment":
        return {
          label: "次へ",
          onClick: handlePaymentNext,
          // 本名（full_name）は契約書に必須。入力されるまで次へ進めない。
          disabled: !(currentUserData?.data?.full_name ?? "").trim(),
          rightIcon: "chevron_right",
        };
      case "contract":
        // DM の受領モーダル（OfferReceiveModal）と同一: 長押し承認が済むまで
        // 押せない「引き受ける」。承諾中はラベルのみ「処理中...」に切り替える。
        return {
          label: isAccepting ? "処理中..." : "引き受ける",
          onClick: handleAccept,
          disabled: !isContractConfirmed || isAccepting,
          rightIcon: "send",
        };
    }
  };

  const getSecondaryAction = (): ModalAction | undefined => {
    switch (currentStep) {
      // アカウント（account）は登録直後の最初のステップなので「戻る」は出さない
      // （戻るとパスワード再設定で詰まるため）。
      case "payment":
        return { label: "戻る", onClick: goBack };
      case "contract":
        return {
          label: "情報を修正する",
          onClick: goBack,
          disabled: isAccepting,
        };
      default:
        return undefined;
    }
  };

  return (
    <Modal
      variant="fullscreen"
      headerExtras={
        <Stepper
          stepLabels={STEP_LABELS}
          current={currentStepIndex + 1}
          showCheckOnComplete={true}
        />
      }
      bodyWidth="reading"
      primaryAction={getPrimaryAction()}
      secondaryAction={getSecondaryAction()}
    >
      <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
    </Modal>
  );
};

export default OfferOnboardingWizard;
